import React, { useState, useEffect } from "react";
import { useAlert } from "../../contexts/AlertContext";
import { transactionAPI } from "../../services/api";
import {
  MdAccountBalance,
  MdContentCopy,
  MdCheckCircle,
  MdWarning,
  MdRefresh,
} from "react-icons/md";
import GlobalStyles, { CommonClasses } from "../../global/GlobalStyle";
import getDocumentUrl from "../../utils/getDocumentUrl";

const Deposit = () => {
  const { addAlert } = useAlert();
  const [selectedCoin, setSelectedCoin] = useState("bitcoin");
  const [selectedNetwork, setSelectedNetwork] = useState("bitcoin");
  const [walletAddresses, setWalletAddresses] = useState<any[]>([]);
  const [copiedAddress, setCopiedAddress] = useState("");
  const [amount, setAmount] = useState("");
  const [txid, setTxid] = useState("");
  const [coinPrice, setCoinPrice] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshingPrice, setIsRefreshingPrice] = useState(false);

  const BalanceIcon = MdAccountBalance as React.FC<
    React.SVGProps<SVGSVGElement>
  >;
  const CopyIcon = MdContentCopy as React.FC<React.SVGProps<SVGSVGElement>>;
  const CheckIcon = MdCheckCircle as React.FC<React.SVGProps<SVGSVGElement>>;
  const WarningIcon = MdWarning as React.FC<React.SVGProps<SVGSVGElement>>;
  const RefreshIcon = MdRefresh as React.FC<React.SVGProps<SVGSVGElement>>;

  const coins = [
    {
      id: "bitcoin",
      name: "Bitcoin (BTC)",
      networks: ["bitcoin"],
      symbol: "BTC",
    },
    {
      id: "ethereum",
      name: "Ethereum (ETH)",
      networks: ["erc20"],
      symbol: "ETH",
    },
    {
      id: "tether",
      name: "Tether (USDT)",
      networks: ["erc20", "bep20", "trc20"],
      symbol: "USDT",
    },
    { id: "solana", name: "Solana (SOL)", networks: ["solana"], symbol: "SOL" },
    { id: "ripple", name: "Ripple (XRP)", networks: ["ripple"], symbol: "XRP" },
    {
      id: "binancecoin",
      name: "BNB",
      networks: ["bep20", "bep2"],
      symbol: "BNB",
    },
    {
      id: "stellar",
      name: "Stellar (XLM)",
      networks: ["stellar"],
      symbol: "XLM",
    },
    // Add Zora and Dogecoin
    {
      id: "zora",
      name: "Zora (ZORA)",
      networks: ["zora", "erc20"],
      symbol: "ZORA",
    },
    {
      id: "dogecoin",
      name: "Dogecoin (DOGE)",
      networks: ["dogecoin"],
      symbol: "DOGE",
    },
  ];

  useEffect(() => {
    fetchWalletAddresses();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (selectedCoin) {
      fetchCoinPrice();
      const coin = coins.find((c) => c.id === selectedCoin);
      if (coin) {
        setSelectedNetwork(coin.networks[0]);
      }
    }
    // eslint-disable-next-line
  }, [selectedCoin]);

  const fetchWalletAddresses = async () => {
    try {
      setIsLoading(true);
      const response = await transactionAPI.getWalletAddresses();
      setWalletAddresses(response.data);
    } catch (error: any) {
      console.error("Failed to fetch wallet addresses:", error);
      addAlert({ type: "error", message: "Failed to load wallet addresses" });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCoinPrice = async () => {
    try {
      setIsRefreshingPrice(true);
      const response = await transactionAPI.getCoinPrice(selectedCoin);
      const priceData = response.data;

      if (!priceData.price) {
        throw new Error("Price not found in response");
      }

      setCoinPrice(priceData.price);

      if (priceData.source === "fallback") {
        addAlert({
          type: "warning",
          message: `Using estimated ${selectedCoin.toUpperCase()} price: $${
            priceData.price
          }`,
        });
      }
    } catch (error: any) {
      console.error("Failed to fetch coin price:", error);
      const fallbackPrices: { [key: string]: number } = {
        bitcoin: 45000,
        ethereum: 2500,
        tether: 1,
        solana: 100,
        ripple: 0.6,
        binancecoin: 350,
        stellar: 0.12,
        // Add Zora and Dogecoin fallback prices
        zora: 0.5, // ZORA fallback price (estimated)
        dogecoin: 0.08, // DOGE fallback price
      };

      const fallbackPrice = fallbackPrices[selectedCoin];
      setCoinPrice(fallbackPrice);

      addAlert({
        type: "warning",
        message: `Using estimated ${selectedCoin.toUpperCase()} price: $${fallbackPrice}`,
      });
    } finally {
      setIsRefreshingPrice(false);
    }
  };

  const handleCopyAddress = (address: string) => {
    navigator.clipboard.writeText(address);
    setCopiedAddress(address);
    addAlert({ type: "success", message: "Address copied to clipboard!" });
    setTimeout(() => setCopiedAddress(""), 2000);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!amount || !txid) {
      addAlert({ type: "error", message: "Please fill all required fields" });
      return;
    }

    if (parseFloat(amount) < 10) {
      addAlert({ type: "error", message: "Minimum deposit amount is $10" });
      return;
    }

    if (!coinPrice) {
      addAlert({
        type: "error",
        message: "Unable to get current price. Please try again.",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await transactionAPI.deposit({
        coin: selectedCoin,
        network: selectedNetwork,
        amount: parseFloat(amount),
        txid: txid,
        from_address: "user_wallet",
      });

      addAlert({ type: "success", message: "Deposit submitted for approval!" });
      setAmount("");
      setTxid("");
    } catch (error: any) {
      addAlert({
        type: "error",
        message: error.response?.data?.error || "Failed to submit deposit",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentCoin = coins.find((coin) => coin.id === selectedCoin);
  const currentAddress = walletAddresses.find(
    (addr) =>
      addr.coin === selectedCoin &&
      addr.network === selectedNetwork &&
      addr.is_active,
  );

  const cryptoAmount =
    coinPrice && amount ? (parseFloat(amount) / coinPrice).toFixed(8) : "0";

  return (
    <div className={GlobalStyles.container}>
      {/* Header */}
      <div>
        <h1
          className={`${CommonClasses.textXl} font-bold ${CommonClasses.textGrayStrong} mb-2`}
        >
          Deposit Crypto
        </h1>
        <p className={`${CommonClasses.textGray}`}>
          Enter amount in USD, get the crypto quantity to send, then send it and
          provide the transaction ID.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Deposit Form */}
        <div className={`${GlobalStyles.sectionWrap}`}>
          <h2
            className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-4`}
          >
            Step 1: Enter Deposit Details
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Coin Selection */}
            <div className={GlobalStyles.formGroup}>
              <label className={GlobalStyles.label}>Select Coin</label>
              <select
                value={selectedCoin}
                onChange={(e) => setSelectedCoin(e.target.value)}
                className={GlobalStyles.input}
              >
                {coins.map((coin) => (
                  <option key={coin.id} value={coin.id}>
                    {coin.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Network Selection */}
            <div className={GlobalStyles.formGroup}>
              <label className={GlobalStyles.label}>Select Network</label>
              <select
                value={selectedNetwork}
                onChange={(e) => setSelectedNetwork(e.target.value)}
                className={GlobalStyles.input}
              >
                {currentCoin?.networks.map((network) => (
                  <option key={network} value={network}>
                    {network.toUpperCase()}
                  </option>
                ))}
              </select>
            </div>

            {/* Amount in USD */}
            <div className={GlobalStyles.formGroup}>
              <label className={GlobalStyles.label}>Amount in USD</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                  $
                </span>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  step="0.01"
                  min="10"
                  className={`${GlobalStyles.input} pl-8`}
                  required
                />
              </div>

              {/* Real-time Conversion Display */}
              {amount && coinPrice && (
                <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <div className={`${CommonClasses.flexBetween}`}>
                    <span className="text-sm font-medium text-green-800 dark:text-green-200">
                      Send Exactly:
                    </span>
                    <span className="text-lg font-bold text-green-600 dark:text-green-400">
                      {cryptoAmount} {currentCoin?.symbol}
                    </span>
                  </div>
                  <div className={`${CommonClasses.flexBetween} mt-1`}>
                    <span className="text-xs text-green-700 dark:text-green-300">
                      Current Price:
                    </span>
                    <span className="text-xs font-semibold text-green-600">
                      $
                      {coinPrice.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 6,
                      })}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Transaction ID */}
            {amount && (
              <div className={GlobalStyles.formGroup}>
                <label className={GlobalStyles.label}>
                  Step 2: Enter Transaction ID
                </label>
                <input
                  type="text"
                  value={txid}
                  onChange={(e) => setTxid(e.target.value)}
                  placeholder="Enter transaction hash/ID after sending crypto"
                  className={GlobalStyles.input}
                  required
                />
                <p
                  className={`${CommonClasses.textXs} ${CommonClasses.textGray} mt-1`}
                >
                  Send exactly {cryptoAmount} {currentCoin?.symbol} first, then
                  paste the transaction ID here
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting || !amount || !txid || !currentAddress}
              className={`${GlobalStyles.buttonPrimary} ${
                isSubmitting ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {isSubmitting ? "Submitting..." : "Submit Deposit"}
            </button>
          </form>
        </div>

        {/* Wallet Address & Instructions */}
        <div className="space-y-6">
          {/* Deposit Address */}
          {currentAddress && (
            <div className={`${GlobalStyles.sectionWrap}`}>
              <h2
                className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-4`}
              >
                Send {currentCoin?.symbol} to This Address
              </h2>

              {/* QR Code */}
              {currentAddress.qr_code && (
                <div className="bg-white p-4 rounded-lg border border-gray-300 dark:border-gray-600 flex items-center justify-center mb-4">
                  <img
                    src={getDocumentUrl(currentAddress.qr_code) || ""}
                    alt="QR Code"
                    className="w-48 h-48 object-contain"
                  />
                </div>
              )}

              {/* Address */}
              <div className="mb-4">
                <label className={GlobalStyles.label}>Wallet Address</label>
                <div className="w-full flex gap-2 items-center">
                  <input
                    type="text"
                    value={currentAddress.address}
                    readOnly
                    className={`${GlobalStyles.input} font-mono text-sm`}
                  />
                  <button
                    onClick={() => handleCopyAddress(currentAddress.address)}
                    className={`${GlobalStyles.buttonIconOutline}`}
                  >
                    {copiedAddress === currentAddress.address ? (
                      <CheckIcon className="w-4 h-4 text-green-500" />
                    ) : (
                      <CopyIcon className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              {/* Network Info */}
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <div className={`${CommonClasses.flexBetween}`}>
                  <span
                    className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
                  >
                    Network:
                  </span>
                  <span className="font-semibold text-blue-600 dark:text-blue-400">
                    {selectedNetwork.toUpperCase()}
                  </span>
                </div>
                <div className={`${CommonClasses.flexBetween} mt-1`}>
                  <span
                    className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
                  >
                    Coin:
                  </span>
                  <span className="font-semibold text-gray-800 dark:text-gray-200">
                    {currentCoin?.symbol}
                  </span>
                </div>
                {coinPrice && (
                  <div className={`${CommonClasses.flexBetween} mt-1`}>
                    <span
                      className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
                    >
                      Current Price:
                    </span>
                    <span className="font-semibold text-green-600 dark:text-green-400">
                      ${coinPrice.toLocaleString()}
                    </span>
                  </div>
                )}
              </div>

              {/* Refresh Price Button */}
              <button
                onClick={fetchCoinPrice}
                disabled={isRefreshingPrice}
                className={`${GlobalStyles.buttonOutline} !w-full mt-3 ${
                  isRefreshingPrice ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                <RefreshIcon
                  className={`w-4 h-4 mr-2 ${
                    isRefreshingPrice ? "animate-spin" : ""
                  }`}
                />
                Refresh Price
              </button>
            </div>
          )}

          {/* Deposit Instructions */}
          <div className={`${GlobalStyles.sectionWrap}`}>
            <h2
              className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-4`}
            >
              How to Deposit
            </h2>
            <div className="space-y-3">
              {[
                {
                  step: "1",
                  title: "Enter USD Amount",
                  description: "Input the amount you want to deposit in USD",
                },
                {
                  step: "2",
                  title: "Send Exact Crypto Amount",
                  description: `Send exactly the calculated ${currentCoin?.symbol} amount shown above`,
                },
                {
                  step: "3",
                  title: "Paste Transaction ID",
                  description:
                    "After sending, paste the transaction ID in the form",
                },
                {
                  step: "4",
                  title: "Wait for Confirmation",
                  description:
                    "Your deposit will be processed after blockchain confirmation",
                },
              ].map((item, index) => (
                <div key={index} className={`${CommonClasses.flexStart} gap-3`}>
                  <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xs font-bold">
                      {item.step}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800 dark:text-gray-200">
                      {item.title}
                    </p>
                    <p
                      className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
                    >
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Important Warning */}
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <div className={`${CommonClasses.flexStart} gap-3`}>
              <WarningIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                  Important Security Notice
                </p>
                <p
                  className={`${CommonClasses.textXs} text-yellow-700 dark:text-yellow-300 mt-1`}
                >
                  • Only send {currentCoin?.symbol} on{" "}
                  {selectedNetwork.toUpperCase()} network
                  <br />• Send exactly {cryptoAmount} {currentCoin?.symbol} - no
                  more, no less
                  <br />
                  • Do not send any other cryptocurrency to this address
                  <br />
                  • Double-check the address before sending
                  <br />• Minimum deposit: $10.00
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className={`${GlobalStyles.sectionWrap} text-center`}>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className={`${CommonClasses.textGray} mt-4`}>
            Loading wallet addresses...
          </p>
        </div>
      )}

      {/* No Address Available */}
      {!isLoading && !currentAddress && (
        <div className={`${GlobalStyles.sectionWrap} text-center`}>
          <BalanceIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="font-semibold text-gray-600 dark:text-gray-300 mb-2">
            No Deposit Address Available
          </h3>
          <p className={`${CommonClasses.textGray}`}>
            Please select a different coin/network combination or try again
            later.
          </p>
        </div>
      )}
    </div>
  );
};

export default Deposit;
